import { useCallback, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import type { ThreeEvent } from "@react-three/fiber";
import * as THREE from "three";

import type { HeroPropId } from "./heroWorldConfig";

import type { Vector3Tuple } from "three";

interface HomePropProps {
  id: HeroPropId;
  position: Vector3Tuple;
  /** Fired with client coords on hover/tap; null when the pointer leaves. */
  onHover: (id: HeroPropId | null, x?: number, y?: number) => void;
  children: React.ReactNode;
}

/**
 * Landing-hero interaction wrapper for world objects: a label tooltip and a
 * soft glow on hover, nothing else. The objects themselves never move.
 */
export function HomeProp({ id, position, onHover, children }: HomePropProps) {
  const glowRef = useRef<THREE.Mesh>(null!);
  const hoveredRef = useRef(false);

  const report = useCallback(
    (e: ThreeEvent<PointerEvent> | ThreeEvent<MouseEvent>) => {
      onHover(id, e.nativeEvent.clientX, e.nativeEvent.clientY);
    },
    [id, onHover],
  );

  const handleEnter = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();
      hoveredRef.current = true;
      document.body.style.cursor = "pointer";
      report(e);
    },
    [report],
  );

  const handleMove = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      if (hoveredRef.current) report(e);
    },
    [report],
  );

  const handleLeave = useCallback(() => {
    hoveredRef.current = false;
    document.body.style.cursor = "";
    onHover(null);
  }, [onHover]);

  const handleClick = useCallback(
    (e: ThreeEvent<MouseEvent>) => {
      e.stopPropagation();
      report(e);
    },
    [report],
  );

  useFrame(({ clock }) => {
    if (!glowRef.current) return;
    const material = glowRef.current.material as THREE.MeshBasicMaterial;
    const target = hoveredRef.current ? 0.35 : 0;
    material.opacity += (target - material.opacity) * 0.1;
    glowRef.current.scale.setScalar(1 + Math.sin(clock.elapsedTime * 2.4) * 0.04);
  });

  return (
    <group position={[position[0], position[1], position[2]]}>
      {/* Hover glow ring, sitting just above the sand */}
      <mesh ref={glowRef} position={[0, 0.03, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.9, 1.35, 32]} />
        <meshBasicMaterial
          color="#FFF3C4"
          transparent
          opacity={0}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </mesh>

      <group
        onPointerEnter={handleEnter}
        onPointerMove={handleMove}
        onPointerLeave={handleLeave}
        onClick={handleClick}
      >
        {/* Invisible hitbox for reliable picking */}
        <mesh position={[0, 1, 0]}>
          <boxGeometry args={[2, 2.5, 2]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
        {children}
      </group>
    </group>
  );
}
